import { useState } from "react";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { PageHeader } from "@/components/Layout";
import { Button, ErrorNote, Field, SidePanel, inputClass, useCanWrite } from "@/components/ui";
import { api, errorMessage } from "@/lib/api";
import { useNotesMonth } from "@/lib/queries";
import type { NoteColor, NoteInput, NoteItem, NoteKind } from "@/lib/types";

/*
 * NOTE, PROMEMORIA E CALENDARIO (09/10), condivisi fra gli utenti del tenant.
 *
 * Dal gestionale CFD viene la forma: la griglia mensile di 42 celle che parte
 * dal lunedi', al massimo tre voci per giorno e poi «+N», il dettaglio del
 * giorno in un pannello, i cinque colori. I promemoria con data e ora li' non
 * c'erano: sono nati qui.
 *
 * TRE COSE IN UNA RIGA SOLA (`Note`), distinte da quello che c'e':
 *
 *   - nota libera     -> niente giorno, niente ora: sta nell'elenco sotto;
 *   - nota del giorno -> un giorno: sta nella sua cella;
 *   - promemoria      -> giorno e ORA: sta nella cella del suo giorno, e quando
 *                        l'ora passa senza «fatto» diventa rosso, qui e nel menu.
 *
 * I GIORNI SONO IN ORA ITALIANA, e la griglia non ne calcola nessuno dal fuso
 * del browser: il backend manda il giorno gia' scritto (`day`, "AAAA-MM-GG",
 * Europe/Rome) e l'ora del promemoria in ora italiana (`remindAtLocal`). Il CFD
 * salvava a mezzanotte UTC e leggeva in ora locale: in un fuso dietro UTC ogni
 * nota finiva il giorno prima.
 */

const COLORI: { valore: NoteColor; nome: string; classe: string }[] = [
  { valore: "gold", nome: "Oro", classe: "bg-[#DEB93F]" },
  { valore: "verde", nome: "Verde", classe: "bg-[#10b981]" },
  { valore: "rosso", nome: "Rosso", classe: "bg-[#ef4444]" },
  { valore: "azzurro", nome: "Azzurro", classe: "bg-[#3b82f6]" },
  { valore: "viola", nome: "Viola", classe: "bg-[#a855f7]" },
];
const testoColore: Record<NoteColor, string> = {
  gold: "text-[#DEB93F]",
  verde: "text-[#10b981]",
  rosso: "text-[#ef4444]",
  azzurro: "text-[#3b82f6]",
  viola: "text-[#a855f7]",
};
const pallino = (c: NoteColor) => COLORI.find((x) => x.valore === c)?.classe ?? "bg-faint";

const GIORNI = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

const pad2 = (n: number) => String(n).padStart(2, "0");
/** Il giorno di OGGI in ora italiana, "AAAA-MM-GG": e' quello che si evidenzia, non quello del browser. */
const oggiRoma = () => new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Rome", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());

/** 42 celle, da lunedi'. Solo numeri: nessuna data costruita nel fuso del browser. */
function griglia(anno: number, mese: number): { key: string; giorno: number; delMese: boolean }[] {
  const giorniNelMese = (a: number, m: number) => new Date(Date.UTC(a, m + 1, 0)).getUTCDate();
  const primoGiornoSettimana = (new Date(Date.UTC(anno, mese, 1)).getUTCDay() + 6) % 7; // lunedi' = 0
  const celle: { key: string; giorno: number; delMese: boolean }[] = [];
  const prima = mese === 0 ? { a: anno - 1, m: 11 } : { a: anno, m: mese - 1 };
  const dopo = mese === 11 ? { a: anno + 1, m: 0 } : { a: anno, m: mese + 1 };
  const nPrima = giorniNelMese(prima.a, prima.m);
  for (let i = primoGiornoSettimana - 1; i >= 0; i--) {
    const g = nPrima - i;
    celle.push({ key: `${prima.a}-${pad2(prima.m + 1)}-${pad2(g)}`, giorno: g, delMese: false });
  }
  for (let g = 1; g <= giorniNelMese(anno, mese); g++) celle.push({ key: `${anno}-${pad2(mese + 1)}-${pad2(g)}`, giorno: g, delMese: true });
  let g = 1;
  while (celle.length < 42) celle.push({ key: `${dopo.a}-${pad2(dopo.m + 1)}-${pad2(g)}`, giorno: g++, delMese: false });
  return celle;
}

/** «giovedì 9 ottobre 2026» da "2026-10-09", senza passare dal fuso del browser. */
function giornoLungo(key: string): string {
  const [a, m, g] = key.split("-").map(Number);
  return new Date(Date.UTC(a!, m! - 1, g!, 12)).toLocaleDateString("it-IT", { timeZone: "UTC", weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

/** L'ora del promemoria, gia' in ora italiana dal backend: "2026-10-09T14:30" -> "14:30". */
const oraDi = (n: NoteItem) => n.remindAtLocal?.slice(11, 16) ?? "";

type Modulo = { nota: NoteItem | null; tipo: NoteKind; giorno: string };

export function Notes() {
  const canWrite = useCanWrite();
  const oggi = oggiRoma();
  const [mese, setMese] = useState(oggi.slice(0, 7));
  const [anno, numMese] = mese.split("-").map(Number) as [number, number];
  const q = useNotesMonth(mese);
  const [giornoAperto, setGiornoAperto] = useState<string | null>(null);
  const [modulo, setModulo] = useState<Modulo | null>(null);

  const sposta = (delta: number) => {
    const totale = anno * 12 + (numMese - 1) + delta;
    setMese(`${Math.floor(totale / 12)}-${pad2((totale % 12) + 1)}`);
  };

  const perGiorno = new Map<string, NoteItem[]>();
  for (const n of q.data?.items ?? []) {
    if (!n.day) continue;
    perGiorno.set(n.day, [...(perGiorno.get(n.day) ?? []), n]);
  }
  // Prima i promemoria in ordine d'ora, poi le note del giorno.
  for (const lista of perGiorno.values()) lista.sort((a, b) => (a.remindAtLocal ?? "~").localeCompare(b.remindAtLocal ?? "~"));

  const scaduti = (q.data?.openReminders ?? []).filter((n) => n.due);
  const futuri = (q.data?.openReminders ?? []).filter((n) => !n.due);

  return (
    <div>
      <PageHeader
        title="Note e calendario"
        subtitle="Note, promemoria e calendario condivisi fra gli utenti di questo gestionale. Orari in ora italiana."
        actions={
          canWrite && (
            <Button variant="primary" onClick={() => setModulo({ nota: null, tipo: "FREE", giorno: oggi })}>
              <Plus className="mr-1 inline h-4 w-4" /> Nuova nota
            </Button>
          )
        }
      />
      <div className="space-y-6 px-6 py-4">
        <ErrorNote>{q.error ? errorMessage(q.error) : null}</ErrorNote>

        {scaduti.length > 0 && (
          <section className="rounded-card border border-loss/40 bg-loss/10 p-4" role="status">
            <h2 className="text-sm font-medium text-loss">
              {scaduti.length === 1 ? "Un promemoria scaduto, non ancora fatto" : `${scaduti.length} promemoria scaduti, non ancora fatti`}
            </h2>
            <ul className="mt-2 space-y-1">
              {scaduti.map((n) => (
                <RigaPromemoria key={n.id} n={n} canWrite={canWrite} onEdit={() => setModulo({ nota: n, tipo: "REMINDER", giorno: n.day ?? oggi })} />
              ))}
            </ul>
          </section>
        )}

        <section className="card p-4">
          <div className="mb-3 flex items-center justify-between gap-2">
            <h2 className="text-base font-semibold text-ink">
              {MESI[numMese - 1]} {anno}
            </h2>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="ghost" onClick={() => sposta(-1)} aria-label="Mese precedente">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setMese(oggi.slice(0, 7))}>
                Oggi
              </Button>
              <Button size="sm" variant="ghost" onClick={() => sposta(1)} aria-label="Mese successivo">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-7 gap-px text-xs text-faint">
            {GIORNI.map((g) => (
              <div key={g} className="px-2 py-1">
                {g}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-px overflow-hidden rounded border border-line bg-line">
            {griglia(anno, numMese - 1).map((c) => {
              const voci = perGiorno.get(c.key) ?? [];
              return (
                <button
                  key={c.key}
                  type="button"
                  aria-label={`${giornoLungo(c.key)}${voci.length ? `, ${voci.length} ${voci.length === 1 ? "voce" : "voci"}` : ""}`}
                  onClick={() => setGiornoAperto(c.key)}
                  className={`flex min-h-[88px] flex-col gap-0.5 bg-surface p-1.5 text-left text-xs hover:bg-raised ${c.delMese ? "" : "opacity-40"} ${
                    c.key === oggi ? "ring-1 ring-inset ring-accent" : ""
                  }`}
                >
                  <span className={`tabular-nums ${c.key === oggi ? "font-semibold text-accent" : "text-muted"}`}>{c.giorno}</span>
                  {voci.slice(0, 3).map((n) => (
                    <span key={n.id} className={`flex items-center gap-1 truncate ${n.doneAt ? "text-faint line-through" : n.due ? "text-loss" : testoColore[n.color]}`} title={n.text}>
                      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${pallino(n.color)}`} aria-hidden />
                      {n.remindAt && <span className="tabular-nums">{oraDi(n)}</span>}
                      <span className="truncate">{n.text}</span>
                    </span>
                  ))}
                  {voci.length > 3 && <span className="text-faint">+{voci.length - 3}</span>}
                </button>
              );
            })}
          </div>
        </section>

        <div className="grid gap-6 lg:grid-cols-2">
          <section className="card p-4">
            <h2 className="mb-2 text-sm font-medium text-ink">Promemoria in arrivo</h2>
            {futuri.length === 0 ? (
              <p className="text-sm text-muted">Nessun promemoria aperto. {canWrite && "Crealo con «Nuova nota», scegliendo «Promemoria»."}</p>
            ) : (
              <ul className="space-y-1">
                {futuri.map((n) => (
                  <RigaPromemoria key={n.id} n={n} canWrite={canWrite} onEdit={() => setModulo({ nota: n, tipo: "REMINDER", giorno: n.day ?? oggi })} />
                ))}
              </ul>
            )}
          </section>
          <section className="card p-4">
            <h2 className="mb-2 text-sm font-medium text-ink">Note libere</h2>
            {(q.data?.free ?? []).length === 0 ? (
              <p className="text-sm text-muted">Nessuna nota libera. {canWrite && "Una nota senza giorno né ora finisce qui."}</p>
            ) : (
              <ul className="space-y-2">
                {q.data!.free.map((n) => (
                  <li key={n.id} className="flex items-start gap-2 text-sm">
                    <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${pallino(n.color)}`} aria-hidden />
                    <span className="min-w-0 flex-1 whitespace-pre-wrap text-ink">{n.text}</span>
                    {canWrite && (
                      <Button size="sm" variant="ghost" onClick={() => setModulo({ nota: n, tipo: "FREE", giorno: oggi })}>
                        Modifica
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>

      {giornoAperto && (
        <SidePanel
          open
          title={giornoLungo(giornoAperto)}
          subtitle="Note e promemoria del giorno"
          onClose={() => setGiornoAperto(null)}
          footer={
            canWrite && (
              <Button variant="primary" onClick={() => setModulo({ nota: null, tipo: "DAY", giorno: giornoAperto })}>
                Aggiungi
              </Button>
            )
          }
        >
          {(perGiorno.get(giornoAperto) ?? []).length === 0 ? (
            <p className="text-sm text-muted">Niente in questo giorno.{canWrite && " Aggiungi una nota o un promemoria."}</p>
          ) : (
            <ul className="space-y-3">
              {perGiorno.get(giornoAperto)!.map((n) => (
                <li key={n.id} className="rounded border border-line p-3 text-sm">
                  <div className="flex items-start gap-2">
                    <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${pallino(n.color)}`} aria-hidden />
                    <div className="min-w-0 flex-1">
                      {n.remindAt && (
                        <p className={`text-xs ${n.doneAt ? "text-faint" : n.due ? "text-loss" : "text-muted"}`}>
                          Promemoria alle {oraDi(n)}
                          {n.doneAt ? ` · fatto${n.doneBy ? ` da ${n.doneBy.name}` : ""}` : n.due ? " · scaduto" : ""}
                        </p>
                      )}
                      <p className={`whitespace-pre-wrap ${n.doneAt ? "text-faint line-through" : "text-ink"}`}>{n.text}</p>
                      {n.createdBy && <p className="mt-1 text-xs text-faint">di {n.createdBy.name}</p>}
                    </div>
                  </div>
                  {canWrite && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {n.remindAt && <DoneButton n={n} />}
                      <Button size="sm" variant="ghost" onClick={() => setModulo({ nota: n, tipo: n.kind, giorno: giornoAperto })}>
                        Modifica
                      </Button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </SidePanel>
      )}

      {modulo && <NoteForm modulo={modulo} onClose={() => setModulo(null)} />}
    </div>
  );
}

function RigaPromemoria({ n, canWrite, onEdit }: { n: NoteItem; canWrite: boolean; onEdit: () => void }) {
  return (
    <li className="flex items-start gap-2 text-sm">
      <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${pallino(n.color)}`} aria-hidden />
      <span className={`shrink-0 tabular-nums ${n.due ? "text-loss" : "text-muted"}`}>
        {n.day ? giornoLungo(n.day).replace(/ \d{4}$/, "") : ""} {oraDi(n)}
      </span>
      <span className="min-w-0 flex-1 whitespace-pre-wrap text-ink">{n.text}</span>
      {canWrite && (
        <span className="flex shrink-0 gap-1">
          <DoneButton n={n} />
          <Button size="sm" variant="ghost" onClick={onEdit}>
            Modifica
          </Button>
        </span>
      )}
    </li>
  );
}

function DoneButton({ n }: { n: NoteItem }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  return (
    <>
      <Button
        size="sm"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          setErr(null);
          try {
            await api.notes.done(n.id, !n.doneAt);
          } catch (e) {
            setErr(errorMessage(e));
          } finally {
            setBusy(false);
          }
        }}
      >
        {n.doneAt ? "Riapri" : "Fatto"}
      </Button>
      {err && <span className="text-xs text-loss">{err}</span>}
    </>
  );
}

function NoteForm({ modulo, onClose }: { modulo: Modulo; onClose: () => void }) {
  const n = modulo.nota;
  const [tipo, setTipo] = useState<NoteKind>(n?.kind ?? modulo.tipo);
  const [testo, setTesto] = useState(n?.text ?? "");
  const [colore, setColore] = useState<NoteColor>(n?.color ?? "gold");
  const [giorno, setGiorno] = useState(n?.day ?? modulo.giorno);
  const [ora, setOra] = useState(n?.remindAtLocal?.slice(11, 16) ?? "09:00");
  const [busy, setBusy] = useState<"salva" | "elimina" | null>(null);
  const [conferma, setConferma] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const salva = async () => {
    setError(null);
    if (!testo.trim()) return setError("Scrivi il testo della nota.");
    const corpo: NoteInput = {
      text: testo.trim(),
      color: colore,
      kind: tipo,
      day: tipo === "DAY" ? giorno : null,
      remindAtLocal: tipo === "REMINDER" ? `${giorno}T${ora}` : null,
    };
    setBusy("salva");
    try {
      if (n) await api.notes.update(n.id, corpo);
      else await api.notes.create(corpo);
      onClose();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  const elimina = async () => {
    if (!n) return;
    if (!conferma) return setConferma(true);
    setBusy("elimina");
    try {
      await api.notes.remove(n.id);
      onClose();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <SidePanel
      open
      title={n ? "Modifica nota" : "Nuova nota"}
      subtitle="Condivisa con tutti gli utenti di questo gestionale"
      onClose={onClose}
      footer={
        <>
          {n && (
            <Button variant="ghost" onClick={elimina} disabled={busy !== null} className="mr-auto text-loss">
              {busy === "elimina" ? "Eliminazione…" : conferma ? "Elimina per sempre?" : "Elimina"}
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button variant="primary" onClick={salva} disabled={busy !== null}>
            {busy === "salva" ? "Salvataggio…" : "Salva"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div role="radiogroup" aria-label="Tipo" className="inline-flex overflow-hidden rounded border border-line text-sm">
          {(
            [
              ["FREE", "Nota libera"],
              ["DAY", "Nota del giorno"],
              ["REMINDER", "Promemoria"],
            ] as const
          ).map(([v, l]) => (
            <button
              key={v}
              type="button"
              role="radio"
              aria-checked={tipo === v}
              onClick={() => setTipo(v)}
              className={`px-3 py-1.5 ${tipo === v ? "bg-raised font-medium text-ink" : "text-muted hover:text-ink"}`}
            >
              {l}
            </button>
          ))}
        </div>

        <Field label="Testo" hint={`${testo.length}/2000`}>
          {(id) => <textarea id={id} value={testo} maxLength={2000} rows={5} onChange={(e) => setTesto(e.target.value)} className={inputClass} />}
        </Field>

        {tipo !== "FREE" && (
          <div className="flex flex-wrap gap-3">
            <Field label="Giorno">{(id) => <input id={id} type="date" value={giorno} onChange={(e) => setGiorno(e.target.value)} className={inputClass} />}</Field>
            {tipo === "REMINDER" && (
              <Field label="Ora (italiana)">{(id) => <input id={id} type="time" value={ora} onChange={(e) => setOra(e.target.value)} className={inputClass} />}</Field>
            )}
          </div>
        )}

        <div>
          <p className="mb-1.5 text-sm text-muted">Colore</p>
          <div className="flex gap-2">
            {COLORI.map((c) => (
              <button
                key={c.valore}
                type="button"
                aria-label={c.nome}
                aria-pressed={colore === c.valore}
                onClick={() => setColore(c.valore)}
                className={`h-6 w-6 rounded-full ${c.classe} ${colore === c.valore ? "ring-2 ring-ink ring-offset-2 ring-offset-surface" : ""}`}
              />
            ))}
          </div>
        </div>

        {n?.doneAt && <p className="text-xs text-muted">Segnato fatto{n.doneBy ? ` da ${n.doneBy.name}` : ""}. Spostare l&apos;ora non lo riapre: usa «Riapri».</p>}
        <ErrorNote>{error}</ErrorNote>
      </div>
    </SidePanel>
  );
}
